import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ArticleSEO } from '@/components/feature/SEOHead';
import { seoArticles, type SeoArticle } from '@/mocks/seo-articles';

function renderSection(section: SeoArticle['sections'][0], index: number) {
  switch (section.type) {
    case 'heading':
      if (section.level === 2) {
        return (
          <h2 key={index} className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-12 mb-4">
            {section.content}
          </h2>
        );
      }
      return (
        <h3 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-3">
          {section.content}
        </h3>
      );
    case 'paragraph':
      return (
        <p
          key={index}
          className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: section.content }}
        />
      );
    case 'list':
      return (
        <div key={index} className="mt-4">
          <p className="text-sm md:text-base font-semibold text-foreground-800 mb-3">{section.content}</p>
          <ul className="space-y-2">
            {section.items?.map((item, i) => (
              <li key={i} className="flex gap-3 text-sm md:text-base text-foreground-700">
                <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    case 'table':
      return (
        <div key={index} className="mt-5 overflow-x-auto">
          <p className="text-sm md:text-base font-semibold text-foreground-800 mb-3">{section.content}</p>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b-2 border-foreground-200">
                <th className="py-3 pr-4 text-sm font-semibold text-foreground-900">Feature</th>
                <th className="py-3 pr-4 text-sm font-semibold text-primary-600">LitBuy Spreadsheet</th>
                <th className="py-3 text-sm font-semibold text-foreground-500">Traditional Spreadsheet</th>
              </tr>
            </thead>
            <tbody className="text-sm text-foreground-700">
              {section.tableData?.map((row, i) => (
                <tr key={i} className="border-b border-background-200">
                  <td className="py-3 pr-4 font-medium">{row.feature}</td>
                  <td className="py-3 pr-4 text-primary-600">{row.litbuy}</td>
                  <td className="py-3 text-foreground-500">{row.traditional}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'tip':
      return (
        <div key={index} className="mt-6 p-5 bg-accent-50 border border-accent-200/50 rounded-lg">
          <p className="text-sm text-foreground-700 leading-relaxed">{section.content}</p>
        </div>
      );
    case 'steps':
      return (
        <div key={index} className="mt-4">
          <p className="text-sm md:text-base font-semibold text-foreground-800 mb-3">{section.content}</p>
          <div className="space-y-4">
            {section.items?.map((step, i) => (
              <div key={i} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold">{i + 1}</span>
                <div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function DynamicArticle() {
  const { slug } = useParams<{ slug: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const article = seoArticles.find(a => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24">
          <div className="w-full px-4 md:px-8 py-20">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading text-3xl font-bold text-foreground-900">Article Not Found</h1>
              <p className="mt-4 text-foreground-500">The article you are looking for does not exist.</p>
              <Link to="/" className="inline-flex items-center gap-2 mt-6 bg-primary-500 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
                Back to Home
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ArticleSEO
        title={article.title}
        description={article.description}
        slug={`/article/${article.slug}`}
        datePublished="2026-06-01"
        dateModified="2026-06-20"
        imageUrl={article.imageUrl}
        faqs={article.faq.map(f => ({ question: f.q, answer: f.a }))}
        category="LitBuy Spreadsheet Guides"
      />
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="pt-24 pb-6 bg-background-50">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
                <Link to="/shopping-guide" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">Shopping Guide</Link>
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
                <span className="text-foreground-600 truncate">{article.title}</span>
              </nav>
            </div>
          </div>
        </div>

        {/* Article Hero Image */}
        <div className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="relative w-full aspect-[16/7] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              {/* H1 */}
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>

              {/* Meta */}
              <div className="mt-4 flex items-center gap-4 text-xs text-foreground-400 flex-wrap">
                <span>Updated: June 2026</span>
                <span>·</span>
                <span>{article.readTime}</span>
                <span>·</span>
                <span>By LitBuy Editorial Team</span>
              </div>

              {/* Introduction */}
              <div className="mt-8">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  <strong>{article.title}</strong> — {article.description}
                </p>
              </div>

              {/* Render sections */}
              {article.sections.map((section, index) => renderSection(section, index))}

              {/* FAQ Section */}
              <section className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {article.faq.map((item, index) => (
                    <div key={index} className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      >
                        <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                          {item.q}
                        </span>
                        <span className={`w-5 h-5 flex-shrink-0 flex items-center justify-center text-foreground-400 transition-transform duration-200 ${openFaq === index ? 'rotate-45' : ''}`}>
                          <i className={openFaq === index ? 'ri-subtract-line' : 'ri-add-line'}></i>
                        </span>
                      </button>
                      {openFaq === index && (
                        <div className="px-4 md:px-5 pb-4 md:pb-5">
                          <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* CTA */}
              <div className="mt-14 p-6 md:p-8 bg-primary-500 rounded-lg text-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
                  Ready to Start Shopping Smarter?
                </h2>
                <p className="mt-3 text-sm md:text-base text-white/85 max-w-lg mx-auto leading-relaxed">
                  Visit Kaspreadsheet now to explore thousands of products across all 15+ categories. Apply the LitBuy Spreadsheet methodology to make every purchase count.
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 bg-white text-primary-700 px-6 py-3 rounded-full text-sm font-semibold hover:bg-white/95 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Explore Kaspreadsheet
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-700 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
                    Back to LitBuy Home
                  </Link>
                </div>
              </div>

              {/* Internal links */}
              <div className="mt-10 p-5 bg-background-100 rounded-lg border border-background-200/70">
                <p className="text-sm font-semibold text-foreground-800 mb-3">Related LitBuy Resources:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/what-is-litbuy-spreadsheet" className="text-xs text-primary-600 hover:text-primary-700 underline cursor-pointer whitespace-nowrap">What Is LitBuy Spreadsheet</Link>
                  <span className="text-foreground-300">·</span>
                  <Link to="/article/how-to-use-litbuy-spreadsheet" className="text-xs text-primary-600 hover:text-primary-700 underline cursor-pointer whitespace-nowrap">How to Use</Link>
                  <span className="text-foreground-300">·</span>
                  <Link to="/article/is-litbuy-spreadsheet-safe" className="text-xs text-primary-600 hover:text-primary-700 underline cursor-pointer whitespace-nowrap">Safety Guide</Link>
                  <span className="text-foreground-300">·</span>
                  <Link to="/shopping-guide" className="text-xs text-primary-600 hover:text-primary-700 underline cursor-pointer whitespace-nowrap">Shopping Guide</Link>
                  <span className="text-foreground-300">·</span>
                  <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 underline cursor-pointer whitespace-nowrap">LitBuy Home</Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}